// COMPONENTS & FUNCTIONS
import {
  calculateRestrictionOrificeDiameter,
  calculateRestrictionOrificeDownstreamPressure,
  calculateRestrictionOrificeFlow,
  calculateRestrictionOrificeUpstreamPressure,
} from "../calculations/restrictionOrifice";

// DATA
import { Density } from "../baseQuantities/density";
import { Flow } from "../baseQuantities/flow";
import { Length } from "../baseQuantities/length";
import { Pressure } from "../baseQuantities/pressure";
import { UnitLess } from "../baseQuantities/unitLess";

// SVG & IMAGES
import restrictionOrificeFigure from "../../img/calc-figure-restriction-orifice.svg";

// INTERFACES
import {
  AdvancedCalculator,
  AppData,
} from "../../interface/functionalityInterface";

/*
RESTRICTION ORIFICE CALCULATOR INFORMATION
  Inputs and outputs are stored in coherent SI units before calculation.
  The four grouped calculations reproduce the rearranged incompressible-liquid
  orifice equation in PROC-CAL-0011. Friction losses and elevation differences
  are neglected, and the orifice coefficient normally defaults to 0.62.
*/

export const restrictionOrificeCalc: AdvancedCalculator = {
  type: AppData.ADVANCED_CALCULATOR,
  definitionId: "restriction-orifice",
  updatedAt: "2026-07-27",
  name: "restriction orifice",
  branch: "Fluids",
  difficulty: "complex",
  equation: "Q = C·A₂·√(2·(p₁ − p₂)/ρ)",
  figure: restrictionOrificeFigure,
  inputs: [
    {
      ...Density,
      id: "orifice-density",
      name: "liquid_density",
      type: "numeric",
      value: "998",
      clearValue: "",
      symbol: "ρ",
      section: "general inputs",
    },
    {
      ...UnitLess,
      id: "orifice-coefficient",
      name: "orifice_coefficient",
      value: "0.62",
      clearValue: "0.62",
      symbol: "C",
      section: "general inputs",
    },
    {
      ...Length,
      id: "orifice-flow-diameter",
      name: "flow_orifice_diameter",
      type: "numeric",
      value: "8",
      clearValue: "",
      symbol: "d₂",
      selectedUnit: "mm",
      section: "calculate flow",
    },
    {
      ...Pressure,
      id: "orifice-flow-upstream-pressure",
      name: "flow_upstream_pressure",
      value: "2",
      clearValue: "",
      symbol: "p₁,g",
      selectedUnit: "bar",
      section: "calculate flow",
    },
    {
      ...Pressure,
      id: "orifice-flow-downstream-pressure",
      name: "flow_downstream_pressure",
      value: "0.5",
      clearValue: "",
      symbol: "p₂,g",
      selectedUnit: "bar",
      section: "calculate flow",
    },
    {
      ...Pressure,
      id: "orifice-diameter-upstream-pressure",
      name: "diameter_upstream_pressure",
      value: "2",
      clearValue: "",
      symbol: "p₁,g",
      selectedUnit: "bar",
      section: "calculate orifice diameter",
    },
    {
      ...Pressure,
      id: "orifice-diameter-downstream-pressure",
      name: "diameter_downstream_pressure",
      value: "0.5",
      clearValue: "",
      symbol: "p₂,g",
      selectedUnit: "bar",
      section: "calculate orifice diameter",
    },
    {
      ...Flow,
      id: "orifice-diameter-flow",
      name: "diameter_flow",
      value: "1.95",
      clearValue: "",
      symbol: "Q",
      section: "calculate orifice diameter",
    },
    {
      ...Length,
      id: "orifice-upstream-diameter",
      name: "upstream_orifice_diameter",
      type: "numeric",
      value: "8",
      clearValue: "",
      symbol: "d₂",
      selectedUnit: "mm",
      section: "calculate upstream pressure",
    },
    {
      ...Pressure,
      id: "orifice-upstream-downstream-pressure",
      name: "upstream_downstream_pressure",
      value: "0.5",
      clearValue: "",
      symbol: "p₂,g",
      selectedUnit: "bar",
      section: "calculate upstream pressure",
    },
    {
      ...Flow,
      id: "orifice-upstream-flow",
      name: "upstream_flow",
      value: "1.95",
      clearValue: "",
      symbol: "Q",
      section: "calculate upstream pressure",
    },
    {
      ...Length,
      id: "orifice-downstream-diameter",
      name: "downstream_orifice_diameter",
      type: "numeric",
      value: "8",
      clearValue: "",
      symbol: "d₂",
      selectedUnit: "mm",
      section: "calculate downstream pressure",
    },
    {
      ...Pressure,
      id: "orifice-downstream-upstream-pressure",
      name: "downstream_upstream_pressure",
      value: "3",
      clearValue: "",
      symbol: "p₁,g",
      selectedUnit: "bar",
      section: "calculate downstream pressure",
    },
    {
      ...Flow,
      id: "orifice-downstream-flow",
      name: "downstream_flow",
      value: "1.95",
      clearValue: "",
      symbol: "Q",
      section: "calculate downstream pressure",
    },
  ],
  outputs: [
    {
      ...Flow,
      id: "orifice-calculated-flow",
      name: "calculated_flow",
      decimalsOffset: 2,
      section: "flow result",
    },
    {
      ...Length,
      id: "orifice-calculated-diameter",
      name: "calculated_orifice_diameter",
      selectedUnit: "mm",
      decimalsOffset: 2,
      section: "diameter result",
    },
    {
      ...Pressure,
      id: "orifice-calculated-upstream-pressure",
      name: "calculated_upstream_pressure",
      selectedUnit: "bar",
      decimalsOffset: 3,
      section: "pressure results",
    },
    {
      ...Pressure,
      id: "orifice-calculated-downstream-pressure",
      name: "calculated_downstream_pressure",
      selectedUnit: "bar",
      decimalsOffset: 3,
      section: "pressure results",
    },
  ],
  calculate: (inputs) => {
    const common = {
      density: inputs.liquid_density,
      orificeCoefficient: inputs.orifice_coefficient,
    };
    const flow = calculateRestrictionOrificeFlow({
      ...common,
      orificeDiameter: inputs.flow_orifice_diameter,
      upstreamPressure: inputs.flow_upstream_pressure,
      downstreamPressure: inputs.flow_downstream_pressure,
    });
    const diameter = calculateRestrictionOrificeDiameter({
      ...common,
      flow: inputs.diameter_flow,
      upstreamPressure: inputs.diameter_upstream_pressure,
      downstreamPressure: inputs.diameter_downstream_pressure,
    });
    const upstreamPressure =
      calculateRestrictionOrificeUpstreamPressure({
        ...common,
        orificeDiameter: inputs.upstream_orifice_diameter,
        flow: inputs.upstream_flow,
        downstreamPressure: inputs.upstream_downstream_pressure,
      });
    const downstreamPressure =
      calculateRestrictionOrificeDownstreamPressure({
        ...common,
        orificeDiameter: inputs.downstream_orifice_diameter,
        flow: inputs.downstream_flow,
        upstreamPressure: inputs.downstream_upstream_pressure,
      });

    if (
      flow === null ||
      diameter === null ||
      upstreamPressure === null ||
      downstreamPressure === null
    ) {
      return null;
    }

    const minimumDiameter = Math.max(
      inputs.flow_orifice_diameter * 0.2,
      0.0001
    );
    const maximumDiameter = inputs.flow_orifice_diameter * 2;
    const diameterRange = maximumDiameter - minimumDiameter;
    const points = Array.from({ length: 101 }, (_, index) => {
      const orificeDiameter =
        minimumDiameter + (diameterRange * index) / 100;
      return {
        orifice_diameter: orificeDiameter,
        flow:
          calculateRestrictionOrificeFlow({
            ...common,
            orificeDiameter,
            upstreamPressure: inputs.flow_upstream_pressure,
            downstreamPressure: inputs.flow_downstream_pressure,
          }) ?? NaN,
      };
    });

    return {
      outputs: {
        calculated_flow: flow,
        calculated_orifice_diameter: diameter,
        calculated_upstream_pressure: upstreamPressure,
        calculated_downstream_pressure: downstreamPressure,
      },
      series: points,
    };
  },
  validate: (inputs) => {
    const messages: string[] = [];
    if (inputs.orifice_coefficient > 1) {
      messages.push(
        "The orifice coefficient is normally no greater than 1."
      );
    }
    if (
      inputs.flow_upstream_pressure <=
      inputs.flow_downstream_pressure
    ) {
      messages.push(
        "Flow calculation requires upstream pressure above downstream pressure."
      );
    }
    if (
      inputs.diameter_upstream_pressure <=
      inputs.diameter_downstream_pressure
    ) {
      messages.push(
        "Diameter calculation requires upstream pressure above downstream pressure."
      );
    }
    return messages;
  },
  chart: {
    title: "Restriction-orifice flow over hole diameter",
    xAxis: {
      key: "orifice_diameter",
      label: "Orifice diameter",
      baseUnit: Length.baseUnit,
      selectedUnit: "mm",
      selectedUnitFromInput: "flow_orifice_diameter",
      units: Length.units,
    },
    datasets: [
      {
        key: "flow",
        label: "Flow",
        axisId: "flow",
        baseUnit: Flow.baseUnit,
        selectedUnit: Flow.selectedUnit,
        units: Flow.units,
        borderColor: "#001e1d",
      },
    ],
  },
};
